import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { SwitchLang } from 'components/SwitchLang';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { useIntl } from 'react-intl';
import axios from 'axios';

import {
   getSearchedInstructions,
   getSearchedNews,
   getSearchedPriceList,
   getSearchedProducts
} from 'api';
import SearchList from 'components/SearchList';

import Breadcrumbs from './Breadcrumbs';
import Search from 'components/Search';
import Logo from 'components/Logo';
import Burger from 'ui/Burger';
import Button from 'ui/Button';
import { devices } from 'styles/devices';
import { colors } from 'styles/colors';
import { routes } from 'routes';
import { renderPricePages } from 'pages/PriceView';
import { SignOut } from '../../components/SignOut';

const wrapWithinHtml = (body) => `
  <html lang="pl">
    <head>
      <meta charset="utf-8">
      <link rel="stylesheet" href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css" integrity="sha384-Vkoo8x4CGsO3+Hhxv8T/Q5PaXtkKtu6ug5TOeNV6gBiFeWPGFN9MuhOf23Q9Ifjh" crossOrigin="anonymous" />
      <style>
         .h3 {
          color: ${colors.neutral70};
         }
         h2 {
             display: block;
             font-size: 16px;
             font-weight: 700;
             color: ${colors.primary};
             text-transform: uppercase;
         }
         table {
            width: 100%;
            max-width: 100%;
            margin-bottom: 1rem;
            font-size: 14px;
            color: ${colors.neutral70};
            border-collapse: collapse;
            table-layout: fixed;
         }
      
         thead {
            background: ${colors.primary};
            color: ${colors.white};
      
            > tr {
               background: ${colors.primary};
               color: ${colors.white};
            }
         }
      
         th {
            padding: 0.75rem;
      
            &:not(:first-of-type) {
               border-left: 1px solid ${colors.white};
            }
         }
      
         tbody {
            tr {
               &:nth-of-type(even) {
                  background-color: ${colors.neutral10};
               }
            }
         }
      
         td {
            padding: 0.75rem;
      
            &:not(:first-of-type) {
               border-left: 1px solid ${colors.neutral20};
            }
         }
         .page {
            margin: 2rem 0;
            break-after: page;
         }
         .container {
            padding-left: 0;
            padding-right: 0;
         }
      </style>
    </head>
    <body>
      <div class="container">
         <div class="row">
            <div class="col">
               ${body}
            </div>
         </div>
      </div>
    </body>
  </html>
`;

const Header = ({ handleBurgerClick, active }) => {
   const { pathname } = useLocation();
   const [visible, setVisible] = useState(false);
   const [searchValue, setSearchValue] = useState('');
   const [productsList, setProductsList] = useState([]);
   const intl = useIntl();

   const user = useSelector((s) => s.auth.user);

   useEffect(() => {
      handleBurgerClick(false);
   }, [pathname]);

   useEffect(() => {
      setVisible(false);
      setSearchValue('');
      setProductsList([]);
   }, [pathname]);

   const refInput = useRef(null);

   const priceList = pathname.includes(routes.priceList);

   const handleBlur = () => !searchValue.length && setVisible(false);
   const handleFocus = () => setVisible(true);

   useEffect(() => {
      if (refInput.current === null) return;

      if (visible) {
         refInput.current.focus();
      }
   }, [refInput, visible]);

   useEffect(() => {
      if (searchValue.length < 2) return undefined;

      if (pathname.startsWith('/katalog')) {
         const id = setTimeout(
            () =>
               getSearchedProducts(searchValue).then(({ data }) => {
                  setProductsList(
                     data?.products.map((product) => ({
                        title: product.post_title,
                        path: `/katalog/produkty/${product.ID}`,
                        category: '',
                        id: product.ID
                     }))
                  );
               }),
            500
         );

         return () => clearTimeout(id);
      } else if (pathname.startsWith('/cennik')) {
         const id = setTimeout(
            () =>
               getSearchedPriceList(searchValue).then(({ data }) => {
                  setProductsList(
                     data?.items.map((i) => ({
                        title: i.post_title,
                        path: `/cennik/${i.ID}`,
                        category: '',
                        id: i.ID
                     }))
                  );
               }),
            500
         );

         return () => clearTimeout(id);
      } else if (pathname.startsWith('/instrukcje')) {
         const id = setTimeout(
            () =>
               getSearchedInstructions(searchValue).then(({ data }) => {
                  setProductsList(
                     data?.items.map((i) => ({
                        title: i.post_title,
                        path: `/instrukcje/${i.ID}`,
                        category: '',
                        id: i.ID
                     }))
                  );
               }),
            500
         );

         return () => clearTimeout(id);
      } else if (pathname.startsWith('/news')) {
         setTimeout(
            () =>
               getSearchedNews(searchValue).then(({ data }) => {
                  setProductsList(
                     data?.items.map((i) => ({
                        title: i.post_title,
                        path: `/news/${i.ID}`,
                        category: '',
                        id: i.ID
                     }))
                  );
               }),
            500
         );
      } else {
         throw new Error(`pathname "${pathname}" not supported`);
      }

      setProductsList([]);
   }, [searchValue]);
   // improve pdf implementation
   const pdf = async () => {
      const pricePages = await renderPricePages(intl);
      const html = wrapWithinHtml(ReactDOMServer.renderToStaticMarkup(pricePages));
      // window.open().document.write(html);
      const { data } = await axios({
         method: 'POST',
         url: 'https://www.api.pdf.konsport.com',
         headers: {
            'Content-Type': 'text/html; charset=utf-8',
            'X-Project-Name': 'cennik',
            'X-Pdf-Name': `cennik`,
            'X-Include-Footer': '0',
            'X-Include-Page-Number': '0'
         },
         data: html
      });
      window.open(data.url);
   };

   return (
      <Wrapper>
         <TopBar>
            <Logo />
            <StyledBurger
               active={active}
               onClick={() => handleBurgerClick(!active)}
               type="button"
            />
         </TopBar>
         <StyledColumn visible={visible}>
            {!visible && (
               <SearchButton
                  onClick={() => {
                     setVisible(true);
                     setSearchValue('');
                     setProductsList([]);
                  }}
               >
                  <StyledSearchIcon icon={visible ? faTimes : faSearch} />
               </SearchButton>
            )}
            {visible && (
               <StyledSearch
                  small
                  refInput={refInput}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  handleClose={() => setVisible(false)}
               />
            )}
            {pathname.includes('/cennik') && !visible && (
               <div>
                  <PdfButton type="button" onClick={pdf}>
                     <FormattedMessage id="GeneratePDF" />
                  </PdfButton>
               </div>
            )}
            {!!productsList.length && visible && <StyledSearchList results={productsList} />}
            {user && !visible && (
               <Button as={Link} to={priceList ? routes.catalog : `${routes.priceList}`}>
                  {priceList ? (
                     <FormattedMessage id="CatalogView.Title" />
                  ) : (
                     <FormattedMessage id="CatalogView.PriceList" />
                  )}
               </Button>
            )}
            {!visible && (
               <>
                  <StyledSwitchLang primary />
                  <SignOut />
               </>
            )}
         </StyledColumn>
         {!visible && (
            <Column>
               <Breadcrumbs />
            </Column>
         )}
      </Wrapper>
   );
};

Header.propTypes = {
   handleBurgerClick: PropTypes.func.isRequired,
   active: PropTypes.bool.isRequired
};

const PdfButton = styled(Button)`
   margin-right: 1rem;
`;

const StyledSwitchLang = styled(SwitchLang)`
   margin-left: 20px;
`;

const StyledSearchList = styled(SearchList)`
   position: absolute;
   top: 20px;
   left: 0;
   background: ${colors.white};
   width: 100%;
   border: 1px solid ${colors.neutral20};
   border-radius: 20px;
   box-shadow: 0px 10px 10px -5px rgba(0, 0, 0, 0.25);
`;

const StyledSearchIcon = styled(FontAwesomeIcon)`
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(calc(-50% + 1px), -50%);
`;

const SearchButton = styled.button`
   position: relative;
   z-index: 600;
   display: block;
   width: 40px;
   height: 40px;
   padding: 0;
   border-radius: 50%;
   border: none;
   margin: 0 20px 0 0;
   background: ${colors.neutral20};
   flex-shrink: 0;

   &:focus {
      outline: 0;
   }
`;

const Wrapper = styled.header`
   position: relative;

   ${devices.tabletXL} {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;
   }
`;

const StyledBurger = styled(Burger)`
   margin-right: -9px;
`;

const TopBar = styled.div`
   position: fixed;
   top: 0;
   left: 0;
   right: 0;
   z-index: 2000;
   display: flex;
   align-items: center;
   justify-content: space-between;
   margin: 10px 0;
   padding: 0 20px;
   background-color: ${colors.neutral70};

   &::before {
      content: '';
      display: block;
      position: absolute;
      z-index: -1;
      top: -20px;
      left: -20px;
      height: calc(100% + 30px);
      width: calc(100% + 40px);
      background-color: ${colors.neutral70};
   }

   ${devices.tabletXL} {
      display: none;
      visibility: hidden;
   }
`;

const StyledSearch = styled(Search)`
   flex: 1;
   margin-right: 20px;
`;

const Column = styled.div`
   display: flex;
   justify-content: space-between;
`;

const StyledColumn = styled(Column)`
   position: relative;
   margin: 60px 0 10px;

   ${({ visible }) =>
      visible &&
      css`
         width: 100%;
      `}

   ${devices.tabletXL} {
      margin: 0;
   }
`;

export default Header;
